import {
  ACTION_ARGS,
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  INPUT_FIELD_ARGS,
  INPUT_FORM_ARGS,
  INPUT_GROUP_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { action } from '@ember/object';
import { fn } from '@ember/helper';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import Container from '../core/container.gjs';
import FlexRow from '../layout/flex/row.gjs';
import LabeledBase from './labeled/base.gjs';
import Super from '../core/base.gjs';
import styles from './checkbox.module.scss';

const METADATA = Metadata.define({
  description: `Selection controls for choosing from predefined options.

### Required arguments
The \`@id\` and \`@onClick\` arguments are always required.

#### \`@id\`
The \`@id\` argument is required to properly link the label to the checkbox input.

#### \`@onClick\`
The \`@onClick\` action is required to get the state of the checkbox on user interaction, as well as the \`event\` object from the click event.
It also provides identifying information about the checkbox that was clicked, such as its \`@id\` and \`@value\` values.
These are received as an object with attributes named \`checked\`, \`event\`, \`id\`, and \`value\`.

### Named blocks
This component exposes a \`:label\` named block that can be used for more complex label content than simple text.
If you only need to include text content, then the \`@label\` argument is preferred.`,
  extends: [
    pick(ACTION_ARGS, ['onBlur', 'onChange', { name: 'onClick', required: true }, 'onFocus']),
    pick(COMPONENT_STATE_ARGS, ['disabled', { name: 'id', initialValue: 'styleguide-checkbox' }]),
    pick(BUTTON_ARGS, [{ name: 'label', initialValue: 'Example checkbox' }]),
    pick(INPUT_FIELD_ARGS, [
      { name: 'checked', initialValue: false },
      'indeterminate',
      'inputClasses',
    ]),
    pick(INPUT_FORM_ARGS, ['required', { name: 'value', initialValue: 'value' }]),
    pick(INPUT_GROUP_ARGS, ['groupName']),
  ],
  name: 'Checkbox',
});

export default class SpdsInputCheckbox extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-checkbox';
  styles = styles;
  type = 'checkbox';

  @tracked labeledInput;

  @reads('labeledInput.inputElement') inputElement;
  @reads('args.value') value;

  get maskId() {
    return this.composeSubcomponentId('mask');
  }

  setCheckboxState(checked, indeterminate) {
    this.inputElement.checked = checked;
    this.inputElement.indeterminate = indeterminate;
  }

  setup = () => {
    let { checked, indeterminate } = this.args;

    this.setCheckboxState(checked, indeterminate);
  };

  @action
  onClick({ checked, event }) {
    this.setCheckboxState(checked, event.target.indeterminate);

    this.args.onClick(...arguments);
  }

  <template>
    <LabeledBase
      @additionalArguments={{this.arguments}}
      @component={{this.constructor.metadata.component}}
      @checked={{@checked}}
      @disabled={{@disabled}}
      @groupName={{@groupName}}
      @id={{this.id}}
      @indeterminate={{@indeterminate}}
      @inputClasses={{@inputClasses}}
      @onBlur={{@onBlur}}
      @onChange={{@onChange}}
      @onClick={{this.onClick}}
      @onFocus={{@onFocus}}
      @registerComponent={{fn this.registerComponent "labeledInput"}}
      @required={{@required}}
      @type={{this.type}}
      @unregisterComponent={{fn this.unregisterComponent "labeledInput"}}
      @value={{@value}}
      class={{this.classes}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      <Container
        class="spds-checkbox spds-input-mask"
        data-test-spds-input-checkbox={{this.id}}
        id={{this.maskId}}
      />
      <FlexRow
        @tag="span"
        class="spds-input-checkbox-label-text"
        data-test-spds-input-checkbox-label-text
      >
        {{#if (has-block "label")}}
          {{yield to="label"}}
        {{else if @label}}
          {{@label}}
        {{else}}
          {{yield}}
        {{/if}}
      </FlexRow>
    </LabeledBase>
  </template>
}

export const INPUT_CHECKBOX_METADATA = SpdsInputCheckbox.metadata;
